import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { ITimer } from '../interface/timer';
import { ITransactionTypeFilterSelection } from '../interface/transactionTypes';

interface TimerContextType {
  timer: ITimer | null;
  initialTimer: ITimer | null;
  updateTimer: (timer: ITimer | null, shouldPrefillInitialTimer?: boolean) => void;
  transactionTypeFilterSelection: ITransactionTypeFilterSelection | null;
  updateTransactionTypeFilterSelection: Dispatch<
    SetStateAction<ITransactionTypeFilterSelection | null>
  >;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [timer, setTimer] = useState<ITimer | null>(null);
  const [initialTimer, setInitialTimer] = useState<ITimer | null>(null);
  const [transactionTypeFilterSelection, setTransactionTypeFilterSelection] =
    useState<ITransactionTypeFilterSelection | null>(null);

  const updateTimer = useCallback((data: ITimer | null, shouldPrefillInitialTimer?: boolean) => {
    setTimer(data);
    if (shouldPrefillInitialTimer) {
      setInitialTimer(data);
    }
    // The picker page and the dropdown share the filter choice for as long as the form is open,
    // and the form is over once the timer is reset.
    if (!data) {
      setTransactionTypeFilterSelection(null);
    }
  }, []);

  const timerContextValue = useMemo(
    () => ({
      timer,
      updateTimer,
      initialTimer,
      transactionTypeFilterSelection,
      updateTransactionTypeFilterSelection: setTransactionTypeFilterSelection,
    }),
    [timer, updateTimer, initialTimer, transactionTypeFilterSelection],
  );
  return <TimerContext.Provider value={timerContextValue}>{children}</TimerContext.Provider>;
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};
